import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Plus, Trash2, Pencil, ExternalLink, Loader2, Save, ArrowLeft, Upload, Image as ImageIcon } from 'lucide-react';
import { checkAuth, clearToken } from '../../admin/api';
import {
  adminNewsList, adminNewsById, createNews, updateNews, deleteNews, uploadNewsImage, fetchCategories,
} from '../../news/api';
import AdminTabs from './AdminTabs';
import BrandMark from '../../components/BrandMark';

const today = () => new Date().toISOString().slice(0, 10);
const blank = () => ({
  title: '', slug: '', category: '', coverImage: '', summary: '', content: '',
  author: '少年造物局', publishDate: today(), isFeatured: false, status: 'published',
});

export default function NewsAdmin() {
  const nav = useNavigate();
  const [ready, setReady] = useState(false);
  const [view, setView] = useState('list'); // list | edit
  const [cats, setCats] = useState([]);
  const [toast, setToast] = useState('');
  const flash = (m) => { setToast(m); setTimeout(() => setToast(''), 2600); };

  useEffect(() => {
    (async () => {
      const ok = await checkAuth();
      if (!ok) { nav('/admin/login'); return; }
      fetchCategories().then(setCats).catch(() => {});
      setReady(true);
    })();
  }, [nav]);

  if (!ready) {
    return <div className="flex min-h-screen items-center justify-center text-gray-400"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> 加载中…</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="sticky top-0 z-20 border-b border-gray-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3">
          <div className="flex items-center gap-2.5">
            <BrandMark size="sm" />
            <div className="font-bold text-slate-900 max-sm:hidden">加太教育 · 管理后台</div>
          </div>
          <AdminTabs />
          <div className="flex items-center gap-2">
            <a href="/news" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 max-sm:hidden"><ExternalLink className="h-4 w-4" /> 看新闻</a>
            <button onClick={() => { clearToken(); nav('/admin/login'); }} className="inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 cursor-pointer"><LogOut className="h-4 w-4" /> 退出</button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-8">
        {view === 'list'
          ? <NewsList cats={cats} onNew={() => setView({ mode: 'new' })} onEdit={(id) => setView({ mode: 'edit', id })} flash={flash} />
          : <NewsEditor cats={cats} state={view} onBack={() => setView('list')} flash={flash} />}
      </main>

      {toast && <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm text-white shadow-lg">{toast}</div>}
    </div>
  );
}

function NewsList({ cats, onNew, onEdit, flash }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('全部新闻');
  const [keyword, setKeyword] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { items } = await adminNewsList({ category, keyword });
      setItems(items);
    } catch (e) { flash(e.message); } finally { setLoading(false); }
  }, [category, keyword, flash]);

  useEffect(() => { load(); }, [load]);

  const onDelete = async (n) => {
    if (!window.confirm(`确定删除《${n.title}》吗？删除后前台将不再显示，且不可恢复。`)) return;
    try { await deleteNews(n.id); flash('已删除'); load(); } catch (e) { flash(e.message); }
  };

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900">新闻管理</h1>
          <p className="mt-1 text-sm text-gray-500">共 {items.length} 篇</p>
        </div>
        <button onClick={onNew} className="btn btn-blue inline-flex items-center gap-1.5 py-2 text-sm"><Plus className="h-4 w-4" /> 新增新闻</button>
      </div>

      <div className="mb-4 flex flex-wrap gap-3">
        <select value={category} onChange={(e) => setCategory(e.target.value)} className="form-input max-w-[220px] cursor-pointer">
          <option>全部新闻</option>
          {cats.map((c) => <option key={c}>{c}</option>)}
        </select>
        <input value={keyword} onChange={(e) => setKeyword(e.target.value)} placeholder="搜索标题…" className="form-input max-w-[260px]" />
      </div>

      <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white shadow-soft">
        {loading ? (
          <div className="flex justify-center py-16 text-gray-400"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> 加载中…</div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center text-gray-400">没有匹配的新闻</div>
        ) : (
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead className="bg-gray-50 text-gray-500">
              <tr>
                <th className="px-4 py-3 font-medium">标题</th>
                <th className="px-4 py-3 font-medium">分类</th>
                <th className="px-4 py-3 font-medium">发布时间</th>
                <th className="px-4 py-3 font-medium">状态</th>
                <th className="px-4 py-3 font-medium text-right">操作</th>
              </tr>
            </thead>
            <tbody>
              {items.map((n) => (
                <tr key={n.id} className="border-t border-gray-100">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-slate-900">{n.title} {!!n.isFeatured && <span className="ml-1 rounded bg-orange-100 px-1.5 py-0.5 text-xs text-orange-600">置顶</span>}</div>
                    <div className="text-xs text-gray-400">/{n.slug}</div>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{n.category}</td>
                  <td className="px-4 py-3 text-gray-600">{(n.publishDate || '').slice(0, 10)}</td>
                  <td className="px-4 py-3">
                    {n.status === 'published'
                      ? <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">已发布</span>
                      : <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">草稿</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {n.status === 'published' && (
                        <a href={`/news/${n.slug}`} target="_blank" rel="noreferrer" className="rounded-lg p-2 text-gray-500 hover:bg-gray-100" title="预览"><ExternalLink className="h-4 w-4" /></a>
                      )}
                      <button onClick={() => onEdit(n.id)} className="rounded-lg p-2 text-blue-600 hover:bg-blue-50 cursor-pointer" title="编辑"><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => onDelete(n)} className="rounded-lg p-2 text-red-500 hover:bg-red-50 cursor-pointer" title="删除"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function NewsEditor({ cats, state, onBack, flash }) {
  const isEdit = state.mode === 'edit';
  const [form, setForm] = useState(blank());
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [coverBusy, setCoverBusy] = useState(false);
  const [bodyBusy, setBodyBusy] = useState(false);

  useEffect(() => {
    if (isEdit) {
      adminNewsById(state.id)
        .then((d) => setForm({ ...d, isFeatured: !!d.isFeatured }))
        .catch((e) => flash(e.message))
        .finally(() => setLoading(false));
    }
  }, [isEdit, state.id, flash]);

  const up = (patch) => setForm((f) => ({ ...f, ...patch }));

  const onCover = async (file) => {
    if (!file) return;
    setCoverBusy(true);
    try { const { url } = await uploadNewsImage(file); up({ coverImage: url }); } catch (e) { flash(e.message); } finally { setCoverBusy(false); }
  };
  const onBodyImg = async (file) => {
    if (!file) return;
    setBodyBusy(true);
    try {
      const { url } = await uploadNewsImage(file);
      up({ content: `${form.content || ''}\n<img src="${url}" alt="" />\n` });
      flash('图片已插入正文末尾');
    } catch (e) { flash(e.message); } finally { setBodyBusy(false); }
  };

  const save = async (status) => {
    const payload = { ...form, status };
    if (!payload.title?.trim() || !payload.category?.trim() || !payload.content?.trim()) {
      flash('标题、分类、正文不能为空'); return;
    }
    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(payload.slug || '')) {
      flash('slug 只能是小写字母、数字和连字符，如 ball-balance-system'); return;
    }
    setSaving(true);
    try {
      if (isEdit) { await updateNews(state.id, payload); flash('已保存'); }
      else { await createNews(payload); flash('已创建'); }
      onBack();
    } catch (e) { flash(e.message); } finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-20 text-gray-400"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> 读取中…</div>;

  return (
    <div>
      <button onClick={onBack} className="mb-4 inline-flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-blue-600 cursor-pointer"><ArrowLeft className="h-4 w-4" /> 返回列表</button>
      <h1 className="mb-5 text-2xl font-extrabold text-slate-900">{isEdit ? '编辑新闻' : '新增新闻'}</h1>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* 主体 */}
        <div className="space-y-4 lg:col-span-2">
          <Field label="新闻标题">
            <input value={form.title} onChange={(e) => up({ title: e.target.value })} className="form-input" placeholder="如：板球平衡系统课程案例" />
          </Field>
          <Field label="URL 标识 slug（详情页地址 /news/xxx）">
            <input value={form.slug} onChange={(e) => up({ slug: e.target.value.toLowerCase() })} className="form-input" placeholder="ball-balance-system" />
          </Field>
          <Field label="新闻摘要">
            <textarea value={form.summary} onChange={(e) => up({ summary: e.target.value })} rows={2} className="form-input" placeholder="一句话概括，会显示在卡片和详情页顶部" />
          </Field>
          <Field label="正文内容（支持 HTML，如 <p>段落</p>）">
            <textarea value={form.content} onChange={(e) => up({ content: e.target.value })} rows={14} className="form-input font-mono text-[13px]" placeholder="<p>第一段……</p>" />
            <label className="mt-2 inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:border-blue-300">
              {bodyBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />} 插入正文图片
              <input type="file" accept="image/*" className="hidden" onChange={(e) => onBodyImg(e.target.files[0])} />
            </label>
          </Field>
        </div>

        {/* 侧栏 */}
        <div className="space-y-4">
          <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-soft space-y-4">
            <Field label="分类">
              <select value={form.category} onChange={(e) => up({ category: e.target.value })} className="form-input cursor-pointer">
                <option value="">请选择</option>
                {cats.map((c) => <option key={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="发布时间">
              <input type="date" value={(form.publishDate || '').slice(0, 10)} onChange={(e) => up({ publishDate: e.target.value })} className="form-input cursor-pointer" />
            </Field>
            <Field label="作者">
              <input value={form.author} onChange={(e) => up({ author: e.target.value })} className="form-input" />
            </Field>
            <label className="flex items-center gap-2 text-sm font-medium text-slate-900">
              <input type="checkbox" checked={!!form.isFeatured} onChange={(e) => up({ isFeatured: e.target.checked })} className="h-4 w-4" /> 置顶
            </label>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-soft">
            <span className="mb-2 block text-sm font-medium text-slate-900">封面图片</span>
            <div className="relative flex aspect-[16/10] items-center justify-center overflow-hidden rounded-xl border border-gray-200 bg-gray-100">
              {form.coverImage && <img src={form.coverImage} alt="" className="absolute inset-0 h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />}
              <label className="relative z-10 inline-flex cursor-pointer items-center gap-1.5 rounded-lg bg-white/90 px-3 py-2 text-sm font-medium text-gray-800 shadow hover:bg-white">
                {coverBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} 上传封面
                <input type="file" accept="image/*" className="hidden" onChange={(e) => onCover(e.target.files[0])} />
              </label>
            </div>
            <input value={form.coverImage} onChange={(e) => up({ coverImage: e.target.value })} className="form-input mt-2 text-xs" placeholder="或填图片地址" />
          </div>

          <div className="flex flex-col gap-2">
            <button onClick={() => save('published')} disabled={saving} className="btn btn-blue inline-flex items-center justify-center gap-1.5 disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} 发布到前台
            </button>
            <button onClick={() => save('draft')} disabled={saving} className="btn btn-white disabled:opacity-60">保存为草稿</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-medium text-slate-900">{label}</span>
      {children}
    </label>
  );
}